import { cfgBinds, CfgKey, DEFAULT_KEY_USE, rebindClashes, type CfgBind, type Rebinds } from "@69ly/shared";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./Icon.tsx";

/** Keys that are not letters, by where they sit on the keyboard, as CS2 names them. */
const CODE_NAMES: Record<string, string> = {
  Space: "space",
  Tab: "tab",
  Enter: "enter",
  Backspace: "backspace",
  CapsLock: "capslock",
  ShiftLeft: "lshift",
  ShiftRight: "rshift",
  ControlLeft: "ctrl",
  ControlRight: "rctrl",
  AltLeft: "alt",
  AltRight: "ralt",
  Insert: "ins",
  Delete: "del",
  Home: "home",
  End: "end",
  PageUp: "pgup",
  PageDown: "pgdn",
  ArrowUp: "uparrow",
  ArrowDown: "downarrow",
  ArrowLeft: "leftarrow",
  ArrowRight: "rightarrow",
  Pause: "pause",
  NumpadDecimal: "kp_del",
  NumpadDivide: "kp_divide",
  NumpadMultiply: "kp_multiply",
  NumpadSubtract: "kp_minus",
  NumpadAdd: "kp_plus",
  NumpadEnter: "kp_enter",
  Minus: "-",
  Equal: "=",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: "semicolon",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Backquote: "`",
};

/**
 * The name CS2 gives the key pressed. Letters follow what is printed on the key
 * (a bind on "a" is the A of an AZERTY keyboard too); the other keys follow
 * their place on the keyboard.
 */
export function keyName(e: Pick<KeyboardEvent, "code" | "key">): string | null {
  const letter = /^Key([A-Z])$/.exec(e.code);
  if (letter) return /^[a-z]$/i.test(e.key) ? e.key.toLowerCase() : letter[1].toLowerCase();
  const digit = /^Digit(\d)$/.exec(e.code);
  if (digit) return digit[1];
  const fn = /^F(\d{1,2})$/.exec(e.code);
  if (fn) return `f${fn[1]}`;
  const pad = /^Numpad(\d)$/.exec(e.code);
  if (pad) return `kp_${pad[1]}`;
  return CODE_NAMES[e.code] ?? null;
}

const MOUSE = ["mouse1", "mouse3", "mouse2", "mouse4", "mouse5"];

/** What the player is changing: listening for the next key, mouse button or wheel turn. */
function KeyCatcher({ onKey, onCancel }: { onKey: (key: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const [bad, setBad] = useState(false);
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.closest(".key-type")) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") return onCancel();
      const name = keyName(e);
      if (name) onKey(name);
    };
    window.addEventListener("keydown", down, true);
    return () => window.removeEventListener("keydown", down, true);
  }, [onKey, onCancel]);
  const submitTyped = () => {
    const ok = CfgKey.safeParse(typed);
    if (ok.success) onKey(ok.data);
    else setBad(true);
  };
  return (
    <div className="key-catcher" role="group" aria-label={t("keys.listening")}>
      <button
        type="button"
        className="key-catch-zone"
        autoFocus
        onMouseDown={(e) => {
          e.preventDefault();
          onKey(MOUSE[e.button] ?? "mouse1");
        }}
        onContextMenu={(e) => e.preventDefault()}
        onWheel={(e) => onKey(e.deltaY < 0 ? "mwheelup" : "mwheeldown")}
        onKeyDown={(e) => e.preventDefault()}
      >
        {t("keys.pressKey")}
        <span className="muted small">{t("keys.pressHelp")}</span>
      </button>
      <div className="row key-type">
        <input
          className={`mono${bad ? " is-invalid" : ""}`}
          value={typed}
          maxLength={20}
          placeholder={t("keys.typePlaceholder")}
          aria-label={t("keys.typeLabel")}
          aria-invalid={bad}
          onChange={(e) => {
            setTyped(e.target.value);
            setBad(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitTyped();
            }
            if (e.key === "Escape") onCancel();
          }}
        />
        <button type="button" className="btn btn-secondary btn-small" onClick={submitTyped} disabled={!typed.trim()}>
          {t("keys.use")}
        </button>
        <button type="button" className="btn btn-quiet btn-small" onClick={onCancel}>
          {t("common.cancel")}
        </button>
      </div>
      {bad && <p className="field-error small">{t("keys.badName")}</p>}
    </div>
  );
}

/** A key as a keycap: F10, MOUSE4, /. */
export const Keycap = ({ k, was = false }: { k: string; was?: boolean }) => <kbd className={`keycap${was ? " is-was" : ""}`}>{k.toUpperCase()}</kbd>;

/** The player's key changes, kept in this browser for this config. */
export function useRebinds(storageKey: string): [Rebinds, (r: Rebinds) => void] {
  const [value, setValue] = useState<Rebinds>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? (parsed as Rebinds) : {};
    } catch {
      return {};
    }
  });
  const set = (r: Rebinds) => {
    const clean = Object.fromEntries(Object.entries(r).filter(([a, b]) => a !== b));
    setValue(clean);
    try {
      if (Object.keys(clean).length) window.localStorage.setItem(storageKey, JSON.stringify(clean));
      else window.localStorage.removeItem(storageKey);
    } catch {
      // No storage: the changes last until the page closes.
    }
  };
  return [value, set];
}

/** ?keys= for a download link, or nothing. */
export const keysQuery = (r: Rebinds, joiner: "?" | "&") => (Object.keys(r).length ? `${joiner}keys=${encodeURIComponent(JSON.stringify(r))}` : "");

/**
 * Every key the config binds, to change before downloading: the file (or the
 * pack) then comes with the player's own keys, and the reading above shows them.
 */
export function KeysCard({
  files,
  overrides,
  value,
  onChange,
}: {
  files: { name: string; text: string }[];
  overrides?: Record<string, string>;
  value: Rebinds;
  onChange: (r: Rebinds) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<string | null>(null);
  const binds = useMemo(() => cfgBinds(files, overrides), [files, overrides]);
  const groups = useMemo(() => {
    const m = new Map<string, CfgBind[]>();
    for (const b of binds) m.set(b.key, [...(m.get(b.key) ?? []), b]);
    return [...m];
  }, [binds]);
  // Changes for keys the file no longer has (a new version) do nothing: leave them out.
  const active = Object.fromEntries(Object.entries(value).filter(([k]) => groups.some(([g]) => g === k)));
  const clashes = rebindClashes(binds, active);
  if (groups.length === 0) return null;
  const isPack = files.length > 1;
  const set = (from: string, to: string) => {
    onChange({ ...active, [from]: to });
    setEditing(null);
  };
  const reset = (from: string) => {
    const next = { ...active };
    delete next[from];
    onChange(next);
  };
  const changed = Object.keys(active).length;

  return (
    <section className="card keys-card">
      <div className="keys-head">
        <h2 className="panel-title">{t("keys.title")}</h2>
        {changed > 0 && (
          <button type="button" className="link-button small" onClick={() => onChange({})}>
            {t("keys.resetAll")}
          </button>
        )}
      </div>
      <p className="muted small">{t("keys.help")}</p>
      <ul className="keys-list">
        {groups.map(([key, list]) => {
          const now = active[key] ?? key;
          const clash = clashes.get(now);
          const takesOver = active[key] && DEFAULT_KEY_USE[now];
          return (
            <li key={key} className={`keys-row${active[key] ? " is-changed" : ""}${clash ? " is-clash" : ""}`}>
              <div className="keys-key">
                <button
                  type="button"
                  className="key-button"
                  aria-label={t("keys.change", { key: now.toUpperCase(), action: list[0].action })}
                  aria-expanded={editing === key}
                  onClick={() => setEditing(editing === key ? null : key)}
                >
                  <Keycap k={now} />
                  <span className="key-change small">{t("keys.changeShort")}</span>
                </button>
                {active[key] && (
                  <span className="keys-was small">
                    {t("keys.was")} <Keycap k={key} was />
                    <button type="button" className="btn btn-quiet btn-icon btn-tiny" aria-label={t("keys.reset", { key: key.toUpperCase() })} onClick={() => reset(key)}>
                      <Icon name="undo" size={12} />
                    </button>
                  </span>
                )}
              </div>
              <div className="keys-what">
                {list.map((b, i) => (
                  <div key={i}>
                    <code className="keys-action" title={b.action}>
                      {b.action}
                    </code>
                    {b.explain && <p className="muted small">{b.explain}</p>}
                    {isPack && <p className="muted small mono">{t("keys.inFile", { file: b.file, line: b.line })}</p>}
                  </div>
                ))}
                {clash && <p className="field-error small">{t("keys.clash", { key: now.toUpperCase(), keys: clash.filter((k) => k !== key).map((k) => k.toUpperCase()).join(", ") })}</p>}
                {takesOver && !clash && <p className="keys-warn small">{t("keys.takesOver", { key: now.toUpperCase(), action: takesOver })}</p>}
              </div>
              {editing === key && <KeyCatcher onKey={(k) => set(key, k)} onCancel={() => setEditing(null)} />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
